import { useState } from "react";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import OperationDialog from "@/components/OperationDialog";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { ListChecks, Plus, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useOptions } from "@/lib/useOptions";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Picking() {
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const warehouses = useOptions("/warehouses");
  const customers = useOptions("/customers");

  const complete = async (row, reload) => {
    try { await api.post(`/picking/${row.id}/complete`); toast.success("Pick completed"); reload(); }
    catch { toast.error("Failed"); }
  };

  const cols = [
    { key: "pick_no", label: "Pick No", mono: true },
    { key: "type", label: "Type", render: (r) => <span className="capitalize">{r.type}</span> },
    { key: "order_ref", label: "Order", mono: true },
    { key: "customer_name", label: "Customer" },
    { key: "items", label: "Lines", render: (r) => (r.items || []).length },
    { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
  ];

  const fields = [
    { name: "type", label: "Pick Type", type: "select", required: true,
      options: ["single", "wave", "batch", "zone", "order"].map((v) => ({ value: v, label: v })) },
    { name: "order_ref", label: "Order Reference" },
    { name: "customer_name", label: "Customer", type: "select", options: customers.map((c) => ({ value: c.label, label: c.label })) },
    { name: "warehouse_id", label: "Warehouse", type: "select", required: true, options: warehouses },
  ];

  return (
    <div>
      <PageHeader title="Picking" subtitle="Wave, batch, zone & single picking" icon={ListChecks}
        breadcrumb={["Home", "Operations", "Picking"]}
        actions={can("picking.create") && (
          <Button data-testid="add-pick-btn" onClick={() => setOpen(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-1.5" />New Pick List
          </Button>
        )} />
      <DataTable endpoint="/picking" columns={cols} testId="picking-table" refreshKey={refresh}
        actions={(row, reload) => row.status === "pending" && can("picking.update") && (
          <Button size="sm" variant="outline" data-testid={`complete-pick-${row.id}`} onClick={() => complete(row, reload)}
            className="text-emerald-600 border-emerald-200 hover:bg-emerald-50">
            <CheckCircle2 className="h-4 w-4 mr-1" />Complete
          </Button>
        )} />
      <OperationDialog open={open} onOpenChange={setOpen} title="New Pick List" headerFields={fields}
        endpoint="/picking" onSaved={() => setRefresh((r) => r + 1)} />
    </div>
  );
}
